import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Card, CardContent,
  Alert, MenuItem, Link,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { prefectures } from '../../data/prefectures';

const AthleteRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', prefecture: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as any)?.from || '/athlete/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    if (form.password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    setLoading(true);
    try {
      const res = await authService.athleteRegister({
        email: form.email,
        password: form.password,
        name: form.name,
        prefecture: form.prefecture,
      });
      login(res.data.token, res.data.user);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 6 }}>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
              アスリート新規登録
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: '#666' }}>
              キャリアプロフィールを作成するためのアカウントを作成します
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="氏名"
                fullWidth
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="メールアドレス"
                type="email"
                fullWidth
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="都道府県"
                select
                fullWidth
                value={form.prefecture}
                onChange={(e) => setForm({ ...form, prefecture: e.target.value })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {prefectures.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="パスワード（8文字以上）"
                type="password"
                fullWidth
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="パスワード（確認）"
                type="password"
                fullWidth
                required
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? '登録中...' : 'アカウントを作成'}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2">
                既にアカウントをお持ちの方は{' '}
                <Link component={RouterLink} to="/auth/athlete-login">ログイン</Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AthleteRegisterPage;
